import {useMemo} from "react"
import {useSelector} from "react-redux"
import {authSelectors} from "../slices/authSlice"
import {DateFormat, TimeFormat} from "../types/userSettingsTypes"

export interface DateTimeFormats {
    timeFormat: {
        default: string
        withSeconds: string
        withPeriod: string
        withPeriodAndSeconds: string
    }
    dateFormat: {
        fullTextUnpadded: string
        fullTextPadded: string
        numeric: string
        numericShort: string
        abbreviated: string
        withoutYear: string
        withoutYearUnpadded: string
        withoutYearShort: string
    }
    is12HourFormat: boolean
    isDayFirst: boolean
    dateTimeFormat: {
        default: string
    }
}
export const useDateTimeFormats = (): DateTimeFormats => {
    const timeFormatType = useSelector(authSelectors.getTimeFormat)
    const dateFormatType = useSelector(authSelectors.getDateFormat)

    const is12HourFormat = useMemo(() => timeFormatType === TimeFormat.Hour12, [timeFormatType])

    const isDayFirst = useMemo(() => dateFormatType === DateFormat.DayFirst, [dateFormatType])

    const timeFormat = useMemo(
        () => ({
            default: is12HourFormat ? "hh:mm" : "HH:mm",
            withSeconds: is12HourFormat ? "hh:mm:ss" : "HH:mm:ss",
            withPeriod: is12HourFormat ? "hh:mm A" : "HH:mm",
            withPeriodAndSeconds: is12HourFormat ? "hh:mm:ss A" : "HH:mm:ss",
        }),
        [is12HourFormat]
    )

    const dateFormat = useMemo(() => {
        return {
            fullTextUnpadded: isDayFirst ? "D MMMM YYYY" : "MMMM D, YYYY",
            fullTextPadded: isDayFirst ? "DD MMMM YYYY" : "MMMM DD, YYYY",
            numeric: isDayFirst ? "DD/MM/YYYY" : "MM/DD/YYYY",
            numericShort: isDayFirst ? "DD/MM/YY" : "MM/DD/YY",
            abbreviated: isDayFirst ? "DD MMM YYYY" : "MMM DD, YYYY",
            withoutYear: isDayFirst ? "DD MMMM" : "MMMM DD",
            withoutYearUnpadded: isDayFirst ? "D MMMM" : "MMMM D",
            withoutYearShort: isDayFirst ? "DD MMM" : "MMM DD",
        }
    }, [isDayFirst])

    const dateTimeFormat = useMemo(
        () => ({
            default: `${dateFormat.numeric} ${timeFormat.withPeriod}`,
        }),
        [dateFormat, timeFormat]
    )

    return {
        timeFormat,
        dateFormat,
        is12HourFormat,
        isDayFirst,
        dateTimeFormat,
    }
}
